import React, { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import RangeChart1 from "../components/Dashboardviz/D07/D07Viz01";
import RangeChart2 from "../components/Dashboardviz/D07/D07Viz02";
import RangeChart3 from "../components/Dashboardviz/D07/D07Viz03";
import RangeChart4 from "../components/Dashboardviz/D07/D07Viz04";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import { useFilter } from "../FilterContext";
import { FILTER_CONFIG } from "../config/filterconfig";
import jsonData from "../current-survey-numeric.json";
import "./D07_Range_of_Responses.css";

const D07_Range_of_ResponsesDashboard = () => {
  const { filterCriteria, setFilterCriteria } = useFilter();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [selectedDemographic, setSelectedDemographic] = useState(
    "demographic_category_01"
  );
  const [filterData, setFilterData] = useState({});
  const [legendItems, setLegendItems] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const rotatingColors = [
    "#E69F00",
    "#56B4E9",
    "#009E73",
    "#F0E442",
    "#0072B2",
    "#D55E00",
    "#CC79A7",
    "#E63434",
    "#6A33C8",
    "#2CCB94",
    "#E3C934",
    "#5E35E0",
    "#39A8E6",
    "#A035E6",
    "#E6356B",
  ];

  // Update window width on resize
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(window.innerWidth);
    }, 200);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate filter options based on FILTER_CONFIG
  useEffect(() => {
    const generateFilterOptions = () => {
      const options = {};
      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(jsonData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter(
          (value) => value !== null && value !== undefined
        );

        if (sortOrder) {
          uniqueValues = uniqueValues.sort((a, b) => {
            const indexA = sortOrder.indexOf(a);
            const indexB = sortOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
        } else {
          uniqueValues = uniqueValues.sort((a, b) =>
            a?.localeCompare(b, undefined, { sensitivity: "base" })
          );
        }

        options[fieldName] = uniqueValues;
      });
      return options;
    };

    setFilterData(generateFilterOptions());
  }, []);

  // Generate legend items based on demographic values with correct sorting
  useEffect(() => {
    const demographicConfig = FILTER_CONFIG.find(
      (item) => item.jsonKey === selectedDemographic
    );
    const predefinedSortOrder = demographicConfig?.sortOrder || [];

    let demographicValues = [
      ...new Set(jsonData.map((item) => item[selectedDemographic])),
    ].filter((value) => value !== null && value !== undefined);

    // Apply predefined sorting if available
    if (predefinedSortOrder.length > 0) {
      demographicValues = demographicValues.sort((a, b) => {
        const indexA = predefinedSortOrder.indexOf(a);
        const indexB = predefinedSortOrder.indexOf(b);
        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    } else {
      demographicValues = demographicValues.sort((a, b) => a.localeCompare(b));
    }

    setLegendItems(
      demographicValues.map((value, index) => ({
        color: rotatingColors[index % rotatingColors.length], // Cycle through colors
        label: value,
      }))
    );
  }, [selectedDemographic]);

  const toggleSidebar = useCallback(
    debounce(() => setIsSidebarOpen((prev) => !prev), 300),
    []
  );

  const toggleFilterSidebar = useCallback(
    debounce(() => setIsFilterSidebarOpen((prev) => !prev), 300),
    []
  );

  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  const handleFilterChange = (updatedCriteria) => {
    setFilterCriteria(updatedCriteria);
  };

  const chartComponents = [
    {
      component: (
        <RangeChart1
          selectedDemographic={selectedDemographic}
          filterCriteria={filterCriteria}
          legendItems={legendItems}
        />
      ),
      title: "Precursor: Role Clarity",
    },
    {
      component: (
        <RangeChart2
          selectedDemographic={selectedDemographic}
          filterCriteria={filterCriteria}
          legendItems={legendItems}
        />
      ),
      title: "Precursor: Work Context",
    },
    {
      component: (
        <RangeChart3
          selectedDemographic={selectedDemographic}
          filterCriteria={filterCriteria}
          legendItems={legendItems}
        />
      ),
      title: "Catalyst: Job Satisfaction",
    },
    {
      component: (
        <RangeChart4
          selectedDemographic={selectedDemographic}
          filterCriteria={filterCriteria}
          legendItems={legendItems}
        />
      ),
      title: "Catalyst: Org Commitment",
    },
  ];

  // Fix chart dimensions on resize
  useEffect(() => {
    const fixChartDimensions = () => {
      const charts = document.querySelectorAll('.cciresponse-range-chart-container .apexcharts-canvas');
      charts.forEach(chart => {
        chart.style.width = '100%';
        chart.style.height = '100%';
        chart.style.position = 'relative';
      });
    };
    
    fixChartDimensions();
    window.addEventListener('resize', fixChartDimensions);
    
    // Run again after a short delay to ensure the chart has rendered
    const timeout = setTimeout(fixChartDimensions, 200);
    
    return () => {
      window.removeEventListener('resize', fixChartDimensions);
      clearTimeout(timeout);
    };
  }, [windowWidth, selectedDemographic, filterCriteria]);

  const isSmallScreen = windowWidth < 1200;

  return (
    <div className="cciresponse-range-dashboard-container">
      <Header
        title="Range of Responses by Demographic Category"
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
        refreshPage={refreshPage}
      />
      <div className="cciresponse-range-main-content">
        <NavigationSidebar
          className={
            isSidebarOpen
              ? "navigation-sidebar-open"
              : "navigation-sidebar-closed"
          }
          isOpen={isSidebarOpen}
        />
        <div className="cciresponse-range-content">
          <FilterSidebar
            isOpen={isFilterSidebarOpen}
            className={
              isFilterSidebarOpen
                ? "filter-sidebar-open"
                : "filter-sidebar-closed"
            }
            filterCriteria={filterCriteria}
            filterOptions={filterData}
            handleFilterChange={handleFilterChange}
          />
          
          <div className="custom-legend-container">
            <div className="demographic-selector">
              <label>
                Select Demographic:
                <select
                  value={selectedDemographic}
                  onChange={(e) => setSelectedDemographic(e.target.value)}
                >
                  {FILTER_CONFIG.filter(
                    ({ fieldName }) => fieldName !== "demographic_category_10"
                  ).map(({ fieldName, displayName }) => (
                    <option key={fieldName} value={fieldName}>
                      {displayName}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="custom-legend">
              {legendItems.map((item, index) => (
                <div key={index} className="legend-item">
                  <span
                    className="legend-marker"
                    style={{ backgroundColor: item.color }}
                  ></span>
                  {item.label}
                </div>
              ))}
            </div>
          </div>
          
          <div className="cciresponse-range-charts-wrapper">
            <div className="cciresponse-range-charts-container">
              {chartComponents.map((chart, index) => (
                <div key={index} className="cciresponse-range-chart">
                  <h3 className="cciresponse-range-chart-title">{chart.title}</h3>
                  <div className="cciresponse-range-chart-container">
                    {chart.component}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default D07_Range_of_ResponsesDashboard;